/**
 * @file Tasks view — task management with tags, priorities, filters
 */

import {
  getTasks, addTask, toggleTask, deleteTask, updateTask,
  getTodaysTasks, getUpcomingTasks, getCompletedTasks,
  toggleSubtask, TASK_TAGS
} from '@services/tasks.js';
import { showModal, closeModal } from '@components/modal.js';
import { toast } from '@components/toast.js';
import { setTabBadge } from '@components/bottom-nav.js';

let _activeFilter = 'all';
let _expandedTaskId = null;

function getFilteredTasks() {
  let tasks;
  if (_activeFilter === 'today') {
    tasks = getTodaysTasks();
  } else if (_activeFilter === 'upcoming') {
    tasks = getUpcomingTasks();
  } else if (_activeFilter === 'completed') {
    tasks = getCompletedTasks();
  } else {
    tasks = getTasks();
  }

  // Sort: done at bottom, then by sortOrder, then by date
  tasks.sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    // Priority sort: high > normal > low
    const priOrder = { high: 0, normal: 1, low: 2 };
    const pa = priOrder[a.priority] ?? 1;
    const pb = priOrder[b.priority] ?? 1;
    if (pa !== pb) return pa - pb;
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });

  return tasks;
}

function render() {
  const tasks = getFilteredTasks();
  const totalCount = getTasks().length;
  const todayCount = getTodaysTasks().length;

  // Set badge for tasks tab
  setTimeout(() => setTabBadge('tasks', todayCount), 0);

  const FILTER_CHIPS = [
    { key: 'all',       label: `All (${totalCount})` },
    { key: 'today',     label: `Today (${getTodaysTasks().length})` },
    { key: 'upcoming',  label: `Upcoming (${getUpcomingTasks().length})` },
    { key: 'completed', label: `Done (${getCompletedTasks().length})` },
  ];

  return `
    <div class="view-header">
      <div>
        <h1>Tasks</h1>
        <div class="view-header-sub">${totalCount} total</div>
      </div>
      <button class="btn btn-gold btn-sm" id="add-task-btn">+ Add</button>
    </div>

    <!-- Quick Add -->
    <div class="task-quick-add" id="task-quick-add">
      <input type="text" id="task-quick-input" placeholder="Quick add task..." />
      <button class="btn btn-gold btn-sm" id="task-quick-submit" style="flex-shrink:0;">Add</button>
    </div>

    <div class="filter-chips" id="tasks-filters">
      ${FILTER_CHIPS.map(f =>
        `<span class="chip ${_activeFilter === f.key ? 'active' : ''}" data-filter="${f.key}">${f.label}</span>`
      ).join('')}
    </div>

    <div class="task-list" id="task-list">
      ${tasks.length > 0
        ? tasks.map(t => renderTaskItem(t)).join('')
        : `<div class="empty-state">
             <div class="empty-state-icon">\u2705</div>
             <div class="empty-state-text">${_activeFilter === 'completed' ? 'No completed tasks' : 'No tasks'}</div>
           </div>`
      }
    </div>
  `;
}

function renderTaskItem(task) {
  const tag = TASK_TAGS[task.tag] || TASK_TAGS['none'];
  const isExpanded = _expandedTaskId === task.id;
  const priDot = task.priority === 'high' ? '<span style="color:var(--red);font-size:0.7rem;margin-right:4px;">\u25CF</span>'
    : task.priority === 'low' ? '<span style="color:var(--gray-500);font-size:0.7rem;margin-right:4px;">\u25CB</span>'
    : '';
  const subtaskCount = (task.subtasks || []).length;
  const subtaskDone = (task.subtasks || []).filter(s => s.done).length;
  const dateStr = task.date || '';

  return `
    <div class="task-item ${task.done ? 'task-done' : ''}" data-task-id="${task.id}">
      <div class="task-item-main">
        <label class="task-checkbox-wrap">
          <input type="checkbox" class="task-checkbox" ${task.done ? 'checked' : ''} data-task-id="${task.id}" />
        </label>
        <div class="task-item-content" data-toggle-expand="${task.id}">
          <div class="task-item-text">
            ${priDot}${esc(task.text)}
          </div>
          <div class="task-item-meta">
            ${task.tag && task.tag !== 'none' ? `<span class="badge ${tag.color}" style="font-size:0.6rem;">${tag.label}</span>` : ''}
            ${dateStr ? `<span style="font-size:0.7rem;color:var(--gray-500);">${dateStr}</span>` : ''}
            ${subtaskCount > 0 ? `<span style="font-size:0.7rem;color:var(--gray-500);">${subtaskDone}/${subtaskCount}</span>` : ''}
          </div>
        </div>
      </div>
      ${isExpanded ? renderExpandedTask(task) : ''}
    </div>
  `;
}

function renderExpandedTask(task) {
  const subtasks = task.subtasks || [];

  return `
    <div class="task-expanded">
      ${task.subdesc ? `<div class="task-subdesc">${esc(task.subdesc)}</div>` : ''}

      ${subtasks.length > 0 ? `
        <div class="task-subtasks">
          ${subtasks.map((st, idx) => `
            <label class="task-subtask-item">
              <input type="checkbox" ${st.done ? 'checked' : ''} data-subtask-idx="${idx}" data-task-id="${task.id}" class="subtask-checkbox" />
              <span class="${st.done ? 'task-done-text' : ''}">${esc(st.text || st)}</span>
            </label>
          `).join('')}
        </div>
      ` : ''}

      <div class="task-expanded-actions">
        <button class="btn btn-sm btn-outline" data-edit-task="${task.id}">Edit</button>
        <button class="btn btn-sm btn-danger" data-delete-task="${task.id}">Delete</button>
      </div>
    </div>
  `;
}

function mount() {
  // Quick add
  const quickInput = document.getElementById('task-quick-input');
  const quickSubmit = document.getElementById('task-quick-submit');
  if (quickInput && quickSubmit) {
    quickSubmit.addEventListener('click', () => {
      const text = quickInput.value.trim();
      if (!text) return;
      addTask(text);
      quickInput.value = '';
      toast('Task added', 'success');
      refreshView();
    });
    quickInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const text = quickInput.value.trim();
        if (!text) return;
        addTask(text);
        quickInput.value = '';
        toast('Task added', 'success');
        refreshView();
      }
    });
  }

  // Add task button (opens modal with more options)
  document.getElementById('add-task-btn')?.addEventListener('click', showAddTaskModal);

  // Filter chips
  const filters = document.getElementById('tasks-filters');
  if (filters) {
    filters.addEventListener('click', (e) => {
      const chip = e.target.closest('.chip');
      if (!chip) return;
      _activeFilter = chip.dataset.filter;
      refreshView();
    });
  }

  // Task list interactions
  const list = document.getElementById('task-list');
  if (list) {
    // Checkboxes
    list.addEventListener('change', (e) => {
      const cb = e.target.closest('.task-checkbox');
      if (cb) {
        e.stopPropagation();
        toggleTask(cb.dataset.taskId);
        refreshView();
        return;
      }
      const stcb = e.target.closest('.subtask-checkbox');
      if (stcb) {
        e.stopPropagation();
        toggleSubtask(stcb.dataset.taskId, parseInt(stcb.dataset.subtaskIdx));
        refreshView();
        return;
      }
    });

    // Tap to expand
    list.addEventListener('click', (e) => {
      if (e.target.closest('input') || e.target.closest('button') || e.target.closest('label')) return;
      const expandTrigger = e.target.closest('[data-toggle-expand]');
      if (expandTrigger) {
        const taskId = expandTrigger.dataset.toggleExpand;
        _expandedTaskId = _expandedTaskId === taskId ? null : taskId;
        refreshView();
        return;
      }

      // Edit button
      const editBtn = e.target.closest('[data-edit-task]');
      if (editBtn) {
        showEditTaskModal(editBtn.dataset.editTask);
        return;
      }

      // Delete button
      const deleteBtn = e.target.closest('[data-delete-task]');
      if (deleteBtn) {
        showModal('Delete Task', '<p style="color:var(--red);">Delete this task?</p>', [
          { label: 'Cancel', onClick: closeModal },
          { label: 'Delete', type: 'danger', onClick: () => {
            deleteTask(deleteBtn.dataset.deleteTask);
            closeModal();
            toast('Task deleted', 'success');
            _expandedTaskId = null;
            refreshView();
          }},
        ]);
        return;
      }
    });
  }
}

function showAddTaskModal() {
  const tagOptions = Object.entries(TASK_TAGS).map(([k, v]) =>
    `<option value="${k}">${v.label}</option>`
  ).join('');

  const today = new Date().toISOString().split('T')[0];

  showModal('Add Task', `
    <div class="form-group">
      <label>Task *</label>
      <input type="text" id="new-task-text" placeholder="What needs to be done?" />
    </div>
    <div class="form-group">
      <label>Tag</label>
      <select id="new-task-tag">
        ${tagOptions}
      </select>
    </div>
    <div class="form-group">
      <label>Priority</label>
      <select id="new-task-priority">
        <option value="normal">Normal</option>
        <option value="high">High</option>
        <option value="low">Low</option>
      </select>
    </div>
    <div class="form-group">
      <label>Due Date</label>
      <input type="date" id="new-task-date" value="${today}" />
    </div>
  `, [
    { label: 'Cancel', onClick: closeModal },
    { label: 'Save', type: 'gold', onClick: () => {
      const text = document.getElementById('new-task-text')?.value?.trim();
      if (!text) { toast('Task text is required', 'error'); return; }
      const tag = document.getElementById('new-task-tag')?.value || 'none';
      const priority = document.getElementById('new-task-priority')?.value || 'normal';
      const date = document.getElementById('new-task-date')?.value || today;
      addTask(text, tag, priority, date);
      closeModal();
      toast('Task added', 'success');
      refreshView();
    }},
  ]);

  setTimeout(() => document.getElementById('new-task-text')?.focus(), 200);
}

function showEditTaskModal(taskId) {
  const task = getTasks().find(t => t.id === taskId);
  if (!task) return;

  const tagOptions = Object.entries(TASK_TAGS).map(([k, v]) =>
    `<option value="${k}"${task.tag === k ? ' selected' : ''}>${v.label}</option>`
  ).join('');

  showModal('Edit Task', `
    <div class="form-group">
      <label>Task</label>
      <input type="text" id="edit-task-text" value="${esc(task.text)}" />
    </div>
    <div class="form-group">
      <label>Description</label>
      <textarea id="edit-task-subdesc" rows="2">${esc(task.subdesc || '')}</textarea>
    </div>
    <div class="form-group">
      <label>Tag</label>
      <select id="edit-task-tag">${tagOptions}</select>
    </div>
    <div class="form-group">
      <label>Priority</label>
      <select id="edit-task-priority">
        <option value="normal"${task.priority === 'normal' ? ' selected' : ''}>Normal</option>
        <option value="high"${task.priority === 'high' ? ' selected' : ''}>High</option>
        <option value="low"${task.priority === 'low' ? ' selected' : ''}>Low</option>
      </select>
    </div>
    <div class="form-group">
      <label>Due Date</label>
      <input type="date" id="edit-task-date" value="${task.date || ''}" />
    </div>
  `, [
    { label: 'Cancel', onClick: closeModal },
    { label: 'Save', type: 'gold', onClick: () => {
      const text = document.getElementById('edit-task-text')?.value?.trim();
      if (!text) { toast('Task text is required', 'error'); return; }
      updateTask(taskId, {
        text,
        subdesc: document.getElementById('edit-task-subdesc')?.value?.trim() || '',
        tag: document.getElementById('edit-task-tag')?.value || 'none',
        priority: document.getElementById('edit-task-priority')?.value || 'normal',
        date: document.getElementById('edit-task-date')?.value || '',
      });
      closeModal();
      toast('Task updated', 'success');
      refreshView();
    }},
  ]);
}

function refreshView() {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render();
    mount();
  }
}

function unmount() {}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default { render, mount, unmount };
